import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Box,
  Typography,
  Autocomplete,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Fade,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StraightenIcon from "@mui/icons-material/Straighten";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import { getData } from "../../../services/library";
import { UrlApi } from "../../../services/apirest";
import { useLegajosContext } from "../../RRHH2/context/LegajoTabsContext";
import { getFotoEmpleado } from "../../RRHH2/features/legajos/services/empleados.api";
import EmployeeAvatar from "../../RRHH2/components/EmployeeAvatar";
import { validateCuil } from "../../RRHH2/features/legajos/utils/validationEmpleado";

function GestionarTalles({ open, empleado, onClose }) {
  const [prendas, setPrendas] = useState([]);
  const [tallesDisponibles, setTallesDisponibles] = useState([]);
  const [tallesAsignados, setTallesAsignados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: "", texto: "" });
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    prenda: null,
  });

  // Cargar datos iniciales
  useEffect(() => {
    if (empleado && open) {
      cargarPrendas();
      cargarTalles();
      cargarTallesEmpleado();
    }
  }, [empleado, open]);

  const cargarPrendas = () => {
    getData("/stock_empleados/prendas", setPrendas);
  };

  const cargarTalles = () => {
    getData("/stock_empleados/talles", setTallesDisponibles);
  };

  const cargarTallesEmpleado = () => {
    getData(`/stock_empleados/${empleado.cuil}/talles`, setTallesAsignados);
  };

  const handleAsignarTalle = async (idPrenda, idTalle) => {
    if (!idTalle) return;

    setLoading(true);
    setMensaje({ tipo: "", texto: "" });

    try {
      const response = await fetch(
        `${UrlApi}/stock_empleados/${empleado.cuil}/tallesParaLegajo`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idPrenda, idTalle }),
        }
      );

      if (response.ok) {
        setMensaje({ tipo: "success", texto: "Los cambios se guardan automáticamente" });
        cargarTallesEmpleado(); // Recargar lista
      } else {
        const error = await response.json();
        throw new Error(error.message || "Error al guardar");
      }
    } catch (error) {
      setMensaje({ tipo: "error", texto: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarTalle = async () => {
    if (!confirmDelete.prenda) return;

    setLoading(true);
    setMensaje({ tipo: "", texto: "" });
    setConfirmDelete({ open: false, prenda: null });

    try {
      const response = await fetch(
        `${UrlApi}/stock_empleados/${empleado.cuil}/talles/${confirmDelete.prenda.id}`,
        { method: "DELETE" }
      );

      if (response.ok) {
        setMensaje({ tipo: "success", texto: "Talle eliminado correctamente" });
        cargarTallesEmpleado();
      } else {
        throw new Error("Error al eliminar");
      }
    } catch (error) {
      setMensaje({ tipo: "error", texto: error.message });
    } finally {
      setLoading(false);
    }
  };

  const obtenerTalleAsignado = (idPrenda) => {
    return tallesAsignados.find((t) => t.idPrenda === idPrenda);
  };

  const tallesAsignadosCount = prendas.filter((p) =>
    obtenerTalleAsignado(p.id)
  ).length;

  const isValidCuil = validateCuil(empleado.cuil);
  //Formularios si tiene cuil valido y otro informativo si no lo tiene
  return (
    <>
      {isValidCuil && (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
              position: "relative",
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "rgba(255,255,255,0.8)",
                "&:hover": {
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            <DialogTitle sx={{ pt: 3, pb: 2 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={2}
              >
                <EmployeeAvatar
                  cuil={empleado.cuil}
                  nombre={empleado.nombre}
                  apellido={empleado.apellido}
                  size={50}
                />

                <Box>
                  <Typography variant="h5" color="#fff" fontWeight={600}>
                    {empleado.nombre} {empleado.apellido}
                  </Typography>
                  <Box display="flex" gap={2} mt={0.5}>
                    <Typography variant="body2" color="rgba(255,255,255,0.85)">
                      Legajo: <strong>{empleado.nro_legajo}</strong>
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.85)">
                      CUIL: <strong>{empleado.cuil}</strong>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </DialogTitle>

            {/* Stats bar */}
            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.15)",
                px: 3,
                py: 1.5,
                display: "flex",
                justifyContent: "center",
                gap: 4,
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <StraightenIcon
                  sx={{ color: "rgba(255,255,255,0.8)", fontSize: 20 }}
                />
                <Typography variant="body2" color="rgba(255,255,255,0.9)">
                  {tallesAsignadosCount} de {prendas.length} talles asignados
                </Typography>
              </Box>
            </Box>
          </Box>

          <DialogContent sx={{ backgroundColor: "#f8f9fa", p: 3 }}>
            <Fade in={!!mensaje.texto}>
              <Box>
                {mensaje.texto && (
                  <Alert
                    severity={mensaje.tipo}
                    sx={{ mb: 2, borderRadius: 2 }}
                    onClose={() => setMensaje({ tipo: "", texto: "" })}
                  >
                    {mensaje.texto}
                  </Alert>
                )}
              </Box>
            </Fade>

            {loading && (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress size={32} />
              </Box>
            )}

            <Grid container spacing={2}>
              {prendas.map((prenda) => {
                const talleActual = obtenerTalleAsignado(prenda.id);
                const isAssigned = !!talleActual;

                return (
                  <Grid item xs={12} sm={6} md={4} key={prenda.id}>
                    <Paper
                      elevation={isAssigned ? 0 : 1}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: isAssigned
                          ? "2px solid #4caf50"
                          : "1px solid #e0e0e0",
                        backgroundColor: isAssigned ? "#f1f8e9" : "#fff",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: 2,
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1.5}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          color={isAssigned ? "success.dark" : "text.primary"}
                          sx={{ fontSize: "0.95rem" }}
                        >
                          {prenda.valor}
                        </Typography>
                        {isAssigned && (
                          <CheckCircleIcon
                            sx={{ color: "success.main", fontSize: 20 }}
                          />
                        )}
                      </Box>

                      <Divider sx={{ mb: 1.5 }} />

                      {talleActual ? (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Chip
                            label={talleActual.talle}
                            color="success"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.85rem",
                              px: 1,
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() =>
                              setConfirmDelete({
                                open: true,
                                prenda: {
                                  id: prenda.id,
                                  nombre: prenda.valor,
                                  talle: talleActual.talle,
                                },
                              })
                            }
                            disabled={loading}
                            sx={{
                              color: "error.light",
                              "&:hover": {
                                color: "error.main",
                                backgroundColor: "error.lighter",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Autocomplete
                          size="small"
                          options={tallesDisponibles}
                          getOptionLabel={(option) => option.valor}
                          onChange={(event, newValue) => {
                            if (newValue) {
                              handleAsignarTalle(prenda.id, newValue.id);
                            }
                          }}
                          disabled={loading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Seleccionar..."
                              variant="outlined"
                              size="small"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1.5,
                                  backgroundColor: "#fafafa",
                                },
                              }}
                            />
                          )}
                        />
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {prendas.length === 0 && !loading && (
              <Box
                textAlign="center"
                py={6}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  border: "1px dashed #ccc",
                }}
              >
                <StraightenIcon
                  sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                />
                <Typography color="text.secondary">
                  No hay prendas disponibles en el sistema
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              backgroundColor: "#fff",
              borderTop: "1px solid #e0e0e0",
              px: 3,
              py: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                px: 4,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Cerrar
            </Button>
          </DialogActions>

          {/* Mini Modal de Confirmación */}
          <Dialog
            open={confirmDelete.open}
            onClose={() => setConfirmDelete({ open: false, prenda: null })}
            maxWidth="xs"
            PaperProps={{
              sx: {
                borderRadius: 3,
                p: 1,
              },
            }}
          >
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "#ffebee",
                  width: 56,
                  height: 56,
                  mx: "auto",
                  mb: 2,
                }}
              >
                <DeleteIcon sx={{ color: "#d32f2f", fontSize: 28 }} />
              </Avatar>

              <Typography variant="h6" fontWeight={600} gutterBottom>
                Eliminar talle
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                ¿Eliminar el talle{" "}
                <strong>{confirmDelete.prenda?.talle}</strong> de{" "}
                <strong>{confirmDelete.prenda?.nombre}</strong>?
              </Typography>

              <Box display="flex" gap={1.5} justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={() =>
                    setConfirmDelete({ open: false, prenda: null })
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    px: 3,
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleEliminarTalle}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    px: 3,
                  }}
                >
                  Eliminar
                </Button>
              </Box>
            </Box>
          </Dialog>
        </Dialog>
      )}
      <Dialog
        open={open && !isValidCuil}
        onClose={onClose}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Avatar
            sx={{
              bgcolor: "#fff3e0",
              width: 64,
              height: 64,
              mx: "auto",
              mb: 2,
            }}
          >
            <PersonIcon sx={{ color: "#f57c00", fontSize: 32 }} />
          </Avatar>

          <Typography variant="h6" fontWeight={600} gutterBottom>
            CUIL inválido
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            El empleado{" "}
            <strong>
              {empleado?.nombre} {empleado?.apellido}
            </strong>
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {empleado?.cuil ? (
              <>
                tiene un CUIL inválido: <strong>{empleado.cuil}</strong>
              </>
            ) : (
              <>no tiene CUIL asignado</>
            )}
          </Typography>

          <Alert severity="warning" sx={{ mb: 3, textAlign: "left" }}>
            <Typography variant="caption">
              Para asignar talles, debe actualizar el CUIL del empleado desde su
              legajo.
            </Typography>
          </Alert>

          <Button
            variant="contained"
            onClick={onClose}
            fullWidth
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cerrar
          </Button>
        </Box>
      </Dialog>
    </>
  );
}

export default GestionarTalles;
